import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { client } from "../apolloClient";
import { useCallback } from "react";

// GraphQL Queries and Mutations
const DB_CONNECTION_QUERIES_AND_MUTATIONS = {
  CREATE_SNOWFLAKE_CONNECTION: gql`
    mutation CreateSnowflakeConnection(
      $userId: ID!
      $account: String!
      $username: String!
      $password: String!
      $warehouse: String!
      $role: String!
      $schema: String!
      $database: String!
    ) {
      CreateSnowflakeConnection(
        UserID: $userId
        account: $account
        username: $username
        password: $password
        warehouse: $warehouse
        role: $role
        schema: $schema
        database: $database
      ) {
        DatasourceID
        tablesData
        status
      }
    }
  `,

  GET_SNOWFLAKE_CONNECTION_DETAILS: gql`
    query GetSnowflakeConnectionDetails($userId: ID!, $userDbId: ID!) {
      GetSnowflakeConnectionDetails(user_id: $userId, user_db_id: $userDbId) {
        user_id
        user_db_id
        tablesData
        account
        schema
        database
      }
    }
  `,

  GET_POSTGRES_CONNECTION_DETAILS: gql`
    query GetPostgresConnectionDetails($userId: ID!) {
      GetPostgresConnectionDetails(user_id: $userId) {
        user_id
        tablesData
        sharedTablesData
      }
    }
  `,

  GET_ALL_SNOWFLAKE_DBS_BY_USER: gql`
    query GetAllSnowflakeDBsByUser($userId: ID!) {
      GetAllSnowflakeDBsByUser(user_id: $userId) {
        user_db_id
        database
      }
    }
  `,

  GET_SNOWFLAKE_TABLE_NAMES: gql`
    query GetSnowflakeTableNames($userId: ID!, $userDbId: ID!) {
      GetSnowflakeTableNames(user_id: $userId, user_db_id: $userDbId)
    }
  `,

  ADD_USER_TABLE_SELECTION: gql`
    mutation AddUserTableSelection($userId: Int!, $userDbId: Int!, $tableNames: [String!]!, $uploadedUserSelected: [String!]) {
      addUserTableSelection(user_id: $userId, user_db_id: $userDbId, tableNames: $tableNames, uploadedUserSelected: $uploadedUserSelected) {
        table_details_id
        user_id
        user_db_id
        table_names
        uploaded_user_selected
        created_on
      }
    }
  `,

  GET_USER_TABLE_SELECTION: gql`
    query GetUserTableSelection($userId: Int!, $userDbId: Int!) {
      getUserTableSelection(user_id: $userId, user_db_id: $userDbId) {
        tableNames {
          tableName
        }
        uploadedUserSelected
      }
    }
  `,

  UPDATE_USER_TABLE_SELECTION: gql`
    mutation UpdateUserTableSelection($userId: Int!, $userDbId: Int!, $tableNames: [String!]!, $uploadedUserSelected: [String!]) {
      updateUserTableSelection(user_id: $userId, user_db_id: $userDbId, tableNames: $tableNames, uploadedUserSelected: $uploadedUserSelected) {
        table_details_id
        user_id
        user_db_id
        table_names
        uploaded_user_selected
        created_on
      }
    }
  `,
};

export const useDbConnection = () => {
  const [createSnowflakeConnectionMutation, { loading: createLoading, error: createError, data: createData }] = useMutation(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.CREATE_SNOWFLAKE_CONNECTION
  );

  const [getSnowflakeConnectionDetails, {
    loading: getDetailsLoading,
    error: getDetailsError,
    data: getDetailsData

  }] = useLazyQuery(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_SNOWFLAKE_CONNECTION_DETAILS,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [getAllSnowflakeDBs, {
    loading: getAllDBsLoading,
    error: getAllDBsError,
    data: getAllDBsData
  }] = useLazyQuery(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_ALL_SNOWFLAKE_DBS_BY_USER
  );

  const [getSnowflakeTableNamesQuery, {
    loading: getTableNamesLoading,
    error: getTableNamesError,
    data: getTableNamesData
  }] = useLazyQuery(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_SNOWFLAKE_TABLE_NAMES
  );

  const [getPostgresConnectionDetailsQuery, {
    loading: getPostgresDetailsLoading,
    error: getPostgresDetailsError,
    data: getPostgresDetailsData
  }] = useLazyQuery(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_POSTGRES_CONNECTION_DETAILS
  );

  const [addUserTableSelectionMutation, { loading: addUserTableSelectionLoading, error: addUserTableSelectionError, data: addUserTableSelectionData }] = useMutation(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.ADD_USER_TABLE_SELECTION
  );

  const [getUserTableSelectionQuery, {
    loading: getUserTableSelectionLoading,
    error: getUserTableSelectionError,
    data: getUserTableSelectionData
  }] = useLazyQuery(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_USER_TABLE_SELECTION,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [updateUserTableSelectionMutation, { loading: updateUserTableSelectionLoading, error: updateUserTableSelectionError, data: updateUserTableSelectionData }] = useMutation(
    DB_CONNECTION_QUERIES_AND_MUTATIONS.UPDATE_USER_TABLE_SELECTION
  );

  const createSnowflakeConnection = async ({
    userId,
    account,
    username,
    password,
    warehouse,
    role,
    schema,
    database,
  }) => {
    try {
      const { data } = await createSnowflakeConnectionMutation({
        variables: {
          userId,
          account,
          username,
          password,
          warehouse,
          role,
          schema,
          database,
        },
      });

      if (!data?.CreateSnowflakeConnection) {
        throw new Error("Snowflake connection failed");
      }

      // Parse the tablesData if it's a string
      let tablesData = data.CreateSnowflakeConnection.tablesData;
      if (typeof tablesData === 'string') {
        try {
          tablesData = JSON.parse(tablesData);
        } catch (e) {
          console.error('Error parsing tablesData:', e);
        }
      }

      return {
        success: true,
        data: {
          ...data.CreateSnowflakeConnection,
          tablesData,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred during Snowflake connection",
      };
    }
  };

  const getConnectionDetails = useCallback(async (userId, userDbId) => {
    try {
      const { data } = await getSnowflakeConnectionDetails({
        variables: {
          userId,
          userDbId,
        },
      });

      if (!data?.GetSnowflakeConnectionDetails) {
        throw new Error("Failed to fetch Snowflake connection details");
      }

      return {
        success: true,
        data: data.GetSnowflakeConnectionDetails,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching connection details",
      };
    }
  }, [getSnowflakeConnectionDetails]);

  // Patch: Use Apollo Client directly for getAllUserDatabases to always fetch fresh data
  const getAllUserDatabases = useCallback(async (userId) => {
    try {
      const { data } = await client.query({
        query: DB_CONNECTION_QUERIES_AND_MUTATIONS.GET_ALL_SNOWFLAKE_DBS_BY_USER,
        variables: { userId },
        fetchPolicy: 'network-only',
      });
      if (!data?.GetAllSnowflakeDBsByUser) {
        throw new Error("Failed to fetch user databases");
      }
      return {
        success: true,
        data: data.GetAllSnowflakeDBsByUser,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching user databases",
      };
    }
  }, []);

  const getSnowflakeTableNames = useCallback(async (userId, userDbId) => {
    try {
      const { data } = await getSnowflakeTableNamesQuery({
        variables: {
          userId,
          userDbId,
        },
      });
      if (!data?.GetSnowflakeTableNames) {
        throw new Error("Failed to fetch Snowflake table names");
      }
      return {
        success: true,
        data: data.GetSnowflakeTableNames,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching table names",
      };
    }
  }, [getSnowflakeTableNamesQuery]);

  const getPostgresConnectionDetails = useCallback(async (userId) => {
    try {
      const { data } = await getPostgresConnectionDetailsQuery({
        variables: {
          userId,
        },
        fetchPolicy: 'network-only',
      });
      if (!data?.GetPostgresConnectionDetails) {
        throw new Error("Failed to fetch Postgres connection details");
      }
      return {
        success: true,
        data: data.GetPostgresConnectionDetails,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching Postgres connection details",
      };
    }
  }, [getPostgresConnectionDetailsQuery]);

  const addUserTableSelection = useCallback(async (userId, userDbId, tableNames, uploadedUserSelected = []) => {
    try {
      const { data } = await addUserTableSelectionMutation({
        variables: {
          userId: parseInt(userId),
          userDbId: parseInt(userDbId),
          tableNames,
          uploadedUserSelected,
        },
      });
      if (!data?.addUserTableSelection) {
        throw new Error('Failed to save table selection');
      }
      return {
        success: true,
        data: data.addUserTableSelection,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An error occurred while saving table selection',
      };
    }
  }, [addUserTableSelectionMutation]);

  const getUserTableSelection = useCallback(async (userId, userDbId) => {
    try {
      const { data } = await getUserTableSelectionQuery({
        variables: {
          userId: parseInt(userId),
          userDbId: parseInt(userDbId),
        },
      });
      if (!data?.getUserTableSelection) {
        throw new Error('Failed to fetch user table selection');
      }
      return {
        success: true,
        data: data.getUserTableSelection.tableNames,
        uploadedUserSelected: data.getUserTableSelection.uploadedUserSelected || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An error occurred while fetching user table selection',
      };
    }
  }, [getUserTableSelectionQuery]);

  const updateUserTableSelection = useCallback(async (userId, userDbId, tableNames, uploadedUserSelected = []) => {
    try {
      const { data } = await updateUserTableSelectionMutation({
        variables: {
          userId: parseInt(userId),
          userDbId: parseInt(userDbId),
          tableNames,
          uploadedUserSelected,
        },
      });
      if (!data?.updateUserTableSelection) {
        throw new Error('Failed to update table selection');
      }
      return {
        success: true,
        data: data.updateUserTableSelection,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An error occurred while updating table selection',
      };
    }
  }, [updateUserTableSelectionMutation]);

  return {
    createSnowflakeConnection,
    getConnectionDetails,
    getAllUserDatabases,
    getSnowflakeTableNames,
    getPostgresConnectionDetails,
    addUserTableSelection,
    getUserTableSelection,
    updateUserTableSelection,
    loading: createLoading || getDetailsLoading || getAllDBsLoading || getTableNamesLoading || getPostgresDetailsLoading || addUserTableSelectionLoading || getUserTableSelectionLoading || updateUserTableSelectionLoading,
    error: createError || getDetailsError || getAllDBsError || getTableNamesError || getPostgresDetailsError || addUserTableSelectionError || getUserTableSelectionError || updateUserTableSelectionError,
    data: createData || getDetailsData || getAllDBsData || getTableNamesData || getPostgresDetailsData || addUserTableSelectionData || getUserTableSelectionData || updateUserTableSelectionData,
  };
}; 